// Core
import {
  Component,
  OnInit,
  AfterViewInit,
  HostBinding,
  HostListener,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  ComponentFactoryResolver,
  Input
} from '@angular/core';
import { AnimationEvent } from '@angular/animations';

// Animations
import { PopupAnimations } from './popup.animations';

// Entities
import { PopupOnAction, POPUP_NODE_CLASS } from './entities';

// Config
import { Popup } from './popup';

@Component({
  selector: 'popup-box',
  template: `<div class="popup" [ngClass]="[position, popup.options.popupClass]" [style.width]="popup.options.width">
  <div class="popup__container">
    <div *ngIf="popup.options.showCloseBtn" class="popup__close" (click)="close()">
      <span>+</span>
    </div>
    <div class="popup__title" *ngIf="popup.options.showTitle">
      <h2>{{popup.title}}</h2>
    </div>
    <div class="popup__content" *ngIf="popup.options.showMessage">
      <p>{{popup.message}}</p>
    </div>
    <div class="popup__injected-component" *ngIf="popup.options.injectComponent">
      <div #componentBody></div>
    </div>
    <div class="popup__actions">
      <button *ngFor="let btn of popup.options.actionButtons" class="popup__actions__btn" [ngClass]="btn.buttonClasses" (click)="doAction(btn.onAction)">{{btn.text}}</button>     
    </div>
  </div>
</div>`,
  styles: [`:host{position:fixed;top:0;z-index:9999;width:100%;height:100%;overflow-y:auto;overflow-x:hidden}.popup{position:absolute;left:50%;right:auto;-webkit-transform:translate(-50%,0);transform:translate(-50%,0);z-index:9999;width:600px;background-color:#fff}.popup.popup-position-top{top:50px;margin-top:0;margin-bottom:50px}.popup.popup-position-bottom{top:auto;bottom:50px;margin-top:50px;margin-bottom:0}.popup.popup-position-center{top:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);margin-top:0;margin-bottom:0}.popup__close{width:50px;height:50px;text-align:center;line-height:50px;position:absolute;top:0;left:auto;right:0;color:#999;font-size:40px;cursor:pointer}.popup__close span{-webkit-transform:rotate(45deg);transform:rotate(45deg);display:inline-block}.popup__title{padding:0 15px}.popup__title h2{margin-top:15px;color:#4d5c6e;font-weight:400}.popup__content{padding:0 15px 15px}.popup__content p{color:#72879e;font-size:16px;line-height:24px}.popup__actions{display:flex}.popup__actions__btn{flex-grow:1;width:50%;border:0;color:#4d5c6e;background-color:#eee;transition:.3s;text-transform:uppercase;font-weight:700;padding:15px;cursor:pointer;outline:0}.popup__actions__btn:hover{background-color:#e1e1e1}.popup__actions__btn.btn-ok{color:#fff;background-color:#00c06d}.popup__actions__btn.btn-ok:hover{background-color:#00a75f}`],
  animations: [PopupAnimations]
})
export class PopupComponent implements OnInit, AfterViewInit {
  @ViewChild('componentBody', { read: ViewContainerRef })
  private componentBodyTarget: ViewContainerRef;
  @Input() popup: any;
  private onActionClicked: (popup: Popup) => void;
  private _actionFlag: any = void 0;
  private popupWidth: any = void 0;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

  public ngOnInit(): void {
    this.popupWidth = this.popup.options.width;
  }

  public ngAfterViewInit(): void {
    const injectComponent = this.popup.options.injectComponent;
    if (typeof injectComponent !== 'undefined') {
      const factory = this.componentFactoryResolver.resolveComponentFactory(injectComponent);
      this.componentBodyTarget.createComponent(factory) as ComponentRef<any>;
    }
  }

  get position(): string {
    return 'popup-position-' + this.popup.options.position;
  }

  @HostBinding('@inOutAnimation')
  get animation() {
    return this.popup.options.animate;
  }

  @HostListener('@inOutAnimation.done', ['$event'])
  public onAnimationEnd(event: AnimationEvent) {
    if (event.toState === 'void' && typeof this._actionFlag !== 'undefined') {
      this.popup.options.callback(this._actionFlag);
    }
  }

  @HostListener('click', ['$event'])
  public onClick(event: any) {
    if (Array.from(event.target.classList).indexOf(POPUP_NODE_CLASS) > -1 && this.popup.options.closeOnOverlay) {
      this.close();
    }
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any) {
    if (this.popupWidth.indexOf('%') === -1) {
      if (event.target.innerWidth <= parseInt(this.popupWidth)) {
        this.popup.options.width = '95%';
      } else {
        this.popup.options.width = this.popupWidth;
      }
    }
  }

  public close(): void {
    if (this.onActionClicked) {
      this.onActionClicked(this.popup);
    }
  }

  public doAction(action: PopupOnAction): void {
    if (typeof action === 'function') {
      this._actionFlag = action();
    } else {
      this._actionFlag = action;
    }
    this.close();
  }
}
